/**
 * Concept Definitions with NGSS Standard Mappings
 * Based on PRD Section 8 and Assignment.md
 */

import type { Concept } from '@/types';

/**
 * Core and Peripheral concepts mapped to NGSS standards
 * MS-LS1-1: All living things are made of cells
 * MS-LS1-2: How organisms work at cell level
 */
export const CONCEPTS: Concept[] = [
  // Core concepts (80% weight)
  {
    id: 'all_living_cells',
    text: 'All living organisms contain one or more cells',
    weight: 'Core',
    standardId: 'MS-LS1-1',
    assessmentCriteria: [
      'Correctly identifies cells in living specimens',
      'Distinguishes living from non-living',
    ],
  },
  {
    id: 'single_celled',
    text: 'Single-celled organisms are composed of one cell that performs all life functions',
    weight: 'Core',
    standardId: 'MS-LS1-1',
    assessmentCriteria: [
      'Identifies unicellular organisms',
      'Explains how single cells sustain life',
    ],
  },
  {
    id: 'multicellular_hierarchy',
    text: 'Multicellular organisms exhibit hierarchy: cells → tissues → organs → systems',
    weight: 'Core',
    standardId: 'MS-LS1-1',
    assessmentCriteria: [
      'Describes cellular organization levels',
      'Builds correct tissue/organ structures',
    ],
  },
  {
    id: 'cell_basic_unit',
    text: 'A cell is the most basic unit of a living organism',
    weight: 'Core',
    standardId: 'MS-LS1-1',
    assessmentCriteria: [
      'Explains why cells are fundamental',
      'Cannot identify living units smaller than cells',
    ],
  },
  {
    id: 'cells_from_cells',
    text: 'Living cells can come only from other living cells',
    weight: 'Core',
    standardId: 'MS-LS1-1',
    assessmentCriteria: [
      'Observes cell division',
      'Understands cell lineage',
    ],
  },
  {
    id: 'cell_theory',
    text: 'Cell theory: all living things made of cells, cells are basic unit, cells come from cells',
    weight: 'Core',
    standardId: 'MS-LS1-1',
    assessmentCriteria: [
      'States three tenets of cell theory',
      'Applies cell theory to explain observations',
    ],
  },

  // Peripheral concepts (20% weight)
  {
    id: 'prokaryotic_eukaryotic',
    text: 'Cells can be prokaryotic (no nucleus) or eukaryotic (has nucleus)',
    weight: 'Peripheral',
    standardId: 'MS-LS1-1',
    assessmentCriteria: [
      'Distinguishes cell types by nucleus presence',
      'Identifies bacteria as prokaryotes',
    ],
  },
  {
    id: 'organelle_functions',
    text: 'Organelles perform specific functions within cells',
    weight: 'Peripheral',
    standardId: 'MS-LS1-2',
    assessmentCriteria: [
      'Names major organelles',
      'Describes organelle functions',
    ],
  },
  {
    id: 'life_functions',
    text: 'Living things perform essential life functions: metabolism, reproduction, response, etc.',
    weight: 'Peripheral',
    standardId: 'MS-LS1-2',
    assessmentCriteria: [
      'Identifies life functions in specimens',
      'Explains why each function is necessary',
    ],
  },
  {
    id: 'cell_specialization',
    text: 'In multicellular organisms, cells specialize for different functions',
    weight: 'Peripheral',
    standardId: 'MS-LS1-1',
    assessmentCriteria: [
      'Recognizes different cell types',
      'Explains why specialization is beneficial',
    ],
  },
  {
    id: 'homeostasis',
    text: 'Cells maintain homeostasis - internal balance despite external changes',
    weight: 'Peripheral',
    standardId: 'MS-LS1-2',
    assessmentCriteria: [
      'Observes cellular response to stimuli',
      'Understands equilibrium maintenance',
    ],
  },
];

/**
 * Map concept IDs to their NGSS standards
 */
export const CONCEPT_TO_STANDARD: Map<string, string[]> = new Map(
  CONCEPTS.map((c) => [c.id, [c.standardId]])
);

/**
 * Get concepts by standard ID
 */
export function getConceptsByStandard(standardId: string): Concept[] {
  return CONCEPTS.filter((c) => c.standardId === standardId);
}

/**
 * Get core concepts only
 */
export function getCoreConcepts(): Concept[] {
  return CONCEPTS.filter((c) => c.weight === 'Core');
}

/**
 * Get concept by ID
 */
export function getConceptById(id: string): Concept | undefined {
  return CONCEPTS.find((c) => c.id === id);
}

/**
 * NGSS Standards covered by this game
 */
export const NGSS_STANDARDS = [
  {
    id: 'MS-LS1-1',
    description:
      'Conduct an investigation to provide evidence that living things are made of cells; either one cell or many different numbers and types of cells.',
    concepts: getConceptsByStandard('MS-LS1-1').map((c) => c.id),
  },
  {
    id: 'MS-LS1-2',
    description:
      'Develop and use a model to describe the function of a cell as a whole and ways parts of cells contribute to the function.',
    concepts: getConceptsByStandard('MS-LS1-2').map((c) => c.id),
  },
];

/**
 * Map level IDs to concepts they assess
 */
export const LEVEL_CONCEPT_MAP: Record<string, string[]> = {
  // Chapter 1: Discovering Cells
  '1-1': ['all_living_cells'],
  '1-2': ['all_living_cells', 'prokaryotic_eukaryotic'],
  '1-3': ['all_living_cells', 'life_functions'],
  '1-boss': ['all_living_cells', 'cell_basic_unit'],

  // Chapter 2: One or Many?
  '2-1': ['single_celled', 'life_functions'],
  '2-2': ['multicellular_hierarchy', 'cell_specialization'],
  '2-3': ['single_celled', 'multicellular_hierarchy'],
  '2-boss': ['single_celled', 'multicellular_hierarchy', 'cell_specialization'],

  // Chapter 3: What Makes Life?
  '3-1': ['life_functions', 'homeostasis'],
  '3-2': ['life_functions', 'homeostasis'],
  '3-3': ['cells_from_cells', 'life_functions'],
  '3-4': ['life_functions'],
  '3-boss': ['life_functions', 'homeostasis'],

  // Chapter 4: Cell Theory
  '4-1': ['all_living_cells', 'cell_theory'],
  '4-2': ['cells_from_cells', 'cell_theory'],
  '4-3': ['cell_basic_unit', 'cell_theory'],
  '4-boss': ['cell_theory'],

  // Chapter 5: Building Life
  '5-1': ['organelle_functions', 'cell_basic_unit'],
  '5-2': ['multicellular_hierarchy', 'cell_specialization'],
  '5-3': ['multicellular_hierarchy'],
  '5-4': ['multicellular_hierarchy', 'cell_specialization', 'life_functions'],
  '5-boss': ['cell_theory', 'multicellular_hierarchy', 'life_functions'],
};

/**
 * Get concepts assessed by a level
 */
export function getConceptsForLevel(levelId: string): Concept[] {
  const conceptIds = LEVEL_CONCEPT_MAP[levelId] || [];
  return conceptIds.map((id) => getConceptById(id)).filter((c): c is Concept => c !== undefined);
}
